"use client";

import type { RouterOutputs } from "@api/trpc/routers/_app";
import {
  ChevronLeft,
  CircleFadingArrowUp,
  LogIn,
  Plus,
  Search,
} from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import posthog from "posthog-js";
import { useState } from "react";
import useAuthModal from "@/features/auth/hooks/use-auth-modal";
import MobileSearchDrawer from "@/features/studysets/components/mobile-search-drawer";
import SearchBar from "@/features/studysets/components/searchbar";
import useNewStudySetModal from "@/features/studysets/hooks/use-new-studyset-modal";
import UserDropdown from "@/features/users/components/user-dropdown";
import usePricingModal, { PAYWALL_TYPES } from "@/lib/hooks/use-pricing-modal";
import { useI18n } from "@/locales/client";
import { Button } from "../ui/button";
import { SidebarTrigger, useSidebar } from "../ui/sidebar";

interface NavProps {
  isSubscribed: boolean;
  user: RouterOutputs["users"]["me"] | null;
  generationCount: number;
}

const Nav = ({ isSubscribed, user, generationCount }: NavProps) => {
  const { onOpen } = usePricingModal();
  const { onOpen: onOpenStudySet } = useNewStudySetModal();
  const { isMobile } = useSidebar();
  const pathname = usePathname();
  const router = useRouter();
  const { onOpen: onOpenAuth } = useAuthModal();
  const t = useI18n();
  const isInStudySet = pathname.match(/\/studysets\/([^/]+)\/edit(\?)?$/i);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  return (
    <nav className="flex items-center justify-between w-full relative">
      {/* Left section with sidebar trigger and back button */}
      <div className="flex items-center gap-2 w-20">
        {isMobile && <SidebarTrigger />}
        {isInStudySet && (
          <Button
            variant="ghost"
            size="sm"
            className="hidden md:flex gap-2 text-muted-foreground hover:text-foreground"
            onClick={() => router.back()}
          >
            <ChevronLeft className="size-4" />
            <span className="text-sm">Back</span>
          </Button>
        )}
      </div>

      {/* Center section with search bar */}
      <div className="container absolute inset-x-0 hidden md:flex">
        <SearchBar />
      </div>

      {/* Right section with create studyset, upgrade button and user dropdown */}
      <div className="flex items-center gap-2 justify-end">
        <Button
          onClick={() => setMobileSearchOpen(true)}
          variant="ghost"
          size="icon"
          aria-label="Open search"
          className="md:hidden text-sidebar-accent-foreground hover:text-sidebar-accent-foreground size-8"
        >
          <Search className="size-4" />
        </Button>
        {user && (isSubscribed || generationCount === 0) && (
          <Button
            onClick={onOpenStudySet}
            variant="ghost"
            className="flex gap-2 items-center hover:text-sidebar-accent-foreground text-sidebar-accent-foreground px-2 h-6"
            size="sm"
          >
            <Plus className="size-4" />
            <span className="hidden sm:inline">{t("create")}</span>
          </Button>
        )}
        {!isSubscribed && user && generationCount > 0 && (
          <Button
            onClick={() => {
              onOpen({
                paywallType: PAYWALL_TYPES.NAV_UPGRADE_BUTTON,
              });
              posthog.capture("paywall_shown", {
                paywallType: PAYWALL_TYPES.NAV_UPGRADE_BUTTON,
                userId: user.id,
              });
            }}
            variant="ghost"
            className="flex gap-2 items-center hover:text-sidebar-accent-foreground text-sidebar-accent-foreground px-2 h-6"
            size="sm"
          >
            <CircleFadingArrowUp className="size-4" />
            <span className="hidden sm:inline">{t("upgrade")}</span>
          </Button>
        )}
        {user ? (
          <UserDropdown
            firstName={user.firstName ?? ""}
            lastName={user.lastName ?? ""}
            profileImg={user.profileImg}
            email={user.email}
          />
        ) : (
          <Button
            onClick={() => onOpenAuth()}
            size={"sm"}
            variant={"ghost"}
            className={
              "flex gap-2 items-center hover:text-sidebar-accent-foreground text-sidebar-accent-foreground px-2 h-6"
            }
          >
            <LogIn className="size-4" />
            <span>{t("auth.signUp")}</span>
          </Button>
        )}
      </div>
      <MobileSearchDrawer
        open={mobileSearchOpen}
        onOpenChange={setMobileSearchOpen}
      />
    </nav>
  );
};

export default Nav;
